import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import pluralize from 'pluralize';
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {AnnotationIcon, EyeIcon} from "@heroicons/react/outline";
import {AnnotationIcon as AnnotationIconSolid, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {truncate} from "services/string";
import Dropdown from "components/Dropdown";
import {GreetingPreview} from "types/Greeting";
import {useNavigate} from "react-router-dom";
import DeleteGreeting from '../Delete';
import BooleanIndicator from "components/BooleanIndicator";

function GreetingList() {
    const navigate = useNavigate()

    const [deletingGreeting, setDeletingGreeting] = useState<GreetingPreview | null>(null);
    const [page, setPage] = useState<number>(1);

    const {data: greetingPage, isLoading} = useQuery(
        ['greetings.all', {page}],
        () => ngage.greetings.filtered({page}),
        {keepPreviousData: true}
    )

    // Prefetch next and previous pages
    prefetch('greetings.all', {page}, ngage.greetings.filtered)

    // Prepare breadcrumbs, metadata and actions
    const breadcrumbs = {'App Content': '/', 'Featured': '/', 'Greetings': '/greetings'}
    const meta = [{icon: AnnotationIconSolid, value: pluralize('greeting', greetingPage?.meta.total || 0, true)}]
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/greetings/new')}>New</Button>

    return (
        <Page
          title="Greetings"
          subTitle={`Daily customisable popup screens for app users`}
          breadcrumbs={breadcrumbs}
          meta={meta}
          actions={actions}
        >
            <Table paginationData={greetingPage} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Title</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.CENTER}>Enabled</TableHeader>
                        <TableHeader>Enabled Since</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={isLoading}>

                    {greetingPage && greetingPage.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={5}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <AnnotationIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No greetings</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : greetingPage?.data.map((greeting, index) => (
                        <TableRow key={greeting.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        {/* TODO: Convert to component */}
                                        <Dropdown position="left">
                                            {[
                                                {name: 'View', icon: EyeIcon, onClick: () => navigate(`/greetings/${greeting.id}`)},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/greetings/${greeting.id}/edit`)},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingGreeting(greeting)}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <AnnotationIcon className="h-5 w-5 mr-2 text-gray-500" /> {truncate(greeting.name, 35)}
                                    </div>
                                </div>
                            </TableData>
                            <TableData>{truncate(greeting.title, 35)}</TableData>
                            <TableData alignment={TableData.alignments.CENTER}>
                                <BooleanIndicator value={greeting.is_enabled} />
                            </TableData>
                            <TableData>{formattedDateTime(greeting.enabled_at) ?? 'N/A'}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {/* TODO: Convert to component */}
                                <Dropdown>
                                    {[
                                        {name: 'View', icon: EyeIcon, onClick: () => navigate(`/greetings/${greeting.id}`)},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/greetings/${greeting.id}/edit`)},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingGreeting(greeting)}
                                    ]}
                                </Dropdown>
                            </TableData>
                        </TableRow>
                    ))}

                </TableBody>
            </Table>

            {/* Delete confirmation modal */}
            <DeleteGreeting
                greeting={deletingGreeting}
                show={!!deletingGreeting}
                onCancel={() => setDeletingGreeting(null)}
                onDelete={() => setDeletingGreeting(null)}
                onDeleteFail={() => setDeletingGreeting(null)}
            />
        </Page>
    );
}

export default GreetingList;